/**
 * News Page Component
 * 
 * Displays financial news articles with infinite scroll pagination.
 * Clean, minimal design with open feel.
 */

import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useInfiniteQuery, useQuery } from '@tanstack/react-query';
import { ExternalLink, RefreshCw, Newspaper } from 'lucide-react';
import { motion } from 'framer-motion';
import { newsService, type NewsArticle } from '../services/news';

/**
 * Format a date string to relative time (e.g., "2h ago", "3d ago")
 */
function formatTimeAgo(dateString: string): string {
  const date = new Date(dateString);
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffMins = Math.floor(diffMs / (1000 * 60));
  const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
  const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));

  if (diffMins < 60) {
    return `${diffMins}m`;
  } else if (diffHours < 24) {
    return `${diffHours}h`;
  } else if (diffDays < 7) {
    return `${diffDays}d`;
  } else {
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  }
}

/**
 * News article card component
 */
function NewsCard({ article, index }: { article: NewsArticle; index: number }) {
  return (
    <motion.a
      href={article.url}
      target="_blank"
      rel="noopener noreferrer"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ delay: Math.min(index * 0.03, 0.2) }}
      className="block px-4 py-4 hover:bg-gray-50 dark:hover:bg-white/5 transition-colors active:bg-gray-100 dark:active:bg-white/10"
    >
      {/* Title */}
      <h3 className="text-[15px] font-medium text-gray-900 dark:text-white leading-snug mb-2">
        {article.title}
      </h3>
      
      {/* Meta row */}
      <div className="flex items-center gap-2 text-xs">
        <span className="px-1.5 py-0.5 rounded bg-blue-100 dark:bg-blue-900/40 text-blue-700 dark:text-blue-300 font-semibold">
          {article.ticker}
        </span>
        <span className="text-gray-400 dark:text-gray-500">{article.source}</span>
        <span className="text-gray-400 dark:text-gray-500">·</span>
        <span className="text-gray-400 dark:text-gray-500">{formatTimeAgo(article.publishedDate)}</span>
        <ExternalLink className="w-3 h-3 ml-auto text-gray-300 dark:text-gray-600" />
      </div>
    </motion.a>
  );
}

/**
 * Empty state when no news articles
 */
function EmptyState({ ticker }: { ticker: string | null }) {
  return (
    <div className="flex flex-col items-center justify-center py-20 px-4">
      <Newspaper className="w-10 h-10 text-gray-300 dark:text-gray-600 mb-3" />
      <p className="text-sm text-gray-500 dark:text-gray-400 text-center">
        {ticker 
          ? `No news for ${ticker}`
          : 'No news available'
        }
      </p>
    </div>
  );
}

/**
 * Loading skeleton for news cards
 */
function LoadingSkeleton() {
  return (
    <div className="px-4 py-4 animate-pulse">
      <div className="h-5 w-full bg-gray-100 dark:bg-gray-800 rounded mb-2" />
      <div className="h-4 w-2/3 bg-gray-100 dark:bg-gray-800 rounded" />
    </div>
  );
}

export default function NewsPage() {
  const [selectedTicker, setSelectedTicker] = useState<string | null>(null);
  const loadMoreRef = useRef<HTMLDivElement>(null);

  // Fetch available tickers for filter
  const { data: tickers = [] } = useQuery({
    queryKey: ['newsTickers'],
    queryFn: () => newsService.getTickers(),
  });

  // Infinite query for news articles
  const {
    data,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    isLoading,
    isError,
    refetch,
    isRefetching,
  } = useInfiniteQuery({
    queryKey: ['news', selectedTicker],
    queryFn: ({ pageParam }) => 
      newsService.getNews({
        ticker: selectedTicker || undefined,
        limit: 20,
        cursor: pageParam,
      }),
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (lastPage) => lastPage.nextCursor ?? undefined,
  });

  // Flatten pages into single array of articles
  const articles = data?.pages.flatMap(page => page.articles) ?? [];

  // Intersection Observer for infinite scroll
  const handleObserver = useCallback(
    (entries: IntersectionObserverEntry[]) => {
      const [target] = entries;
      if (target.isIntersecting && hasNextPage && !isFetchingNextPage) {
        fetchNextPage();
      }
    },
    [fetchNextPage, hasNextPage, isFetchingNextPage]
  );

  useEffect(() => {
    const element = loadMoreRef.current;
    if (!element) return;

    const observer = new IntersectionObserver(handleObserver, {
      threshold: 0.1,
      rootMargin: '100px',
    });

    observer.observe(element);
    return () => observer.disconnect();
  }, [handleObserver]);

  return (
    <div className="min-h-screen bg-white dark:bg-black">
      {/* Header */}
      <div className="sticky top-0 z-10 bg-white/90 dark:bg-black/90 backdrop-blur-lg">
        <div className="px-4 pt-4 pb-3 flex items-center justify-between">
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white">
            News
          </h1>
          <button
            onClick={() => refetch()}
            disabled={isRefetching}
            className="p-2 -mr-2 rounded-full hover:bg-gray-100 dark:hover:bg-white/10 transition-colors disabled:opacity-50"
          >
            <RefreshCw className={`w-5 h-5 text-gray-400 ${isRefetching ? 'animate-spin' : ''}`} />
          </button>
        </div>

        {/* Ticker filter chips */}
        {tickers.length > 0 && (
          <div className="px-4 pb-3 overflow-x-auto scrollbar-hide">
            <div className="flex gap-2">
              <button
                onClick={() => setSelectedTicker(null)}
                className={`px-3 py-1 rounded-full text-sm transition-colors whitespace-nowrap ${
                  selectedTicker === null
                    ? 'bg-gray-900 dark:bg-white text-white dark:text-black font-medium'
                    : 'text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white'
                }`}
              >
                All
              </button>
              {tickers.map((ticker) => (
                <button
                  key={ticker}
                  onClick={() => setSelectedTicker(ticker)}
                  className={`px-3 py-1 rounded-full text-sm transition-colors whitespace-nowrap ${
                    selectedTicker === ticker
                      ? 'bg-gray-900 dark:bg-white text-white dark:text-black font-medium'
                      : 'text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white'
                  }`}
                >
                  {ticker}
                </button>
              ))}
            </div>
          </div>
        )}
        
        {/* Subtle divider */}
        <div className="h-px bg-gray-100 dark:bg-gray-800" />
      </div>

      {/* Content */}
      <div className="pb-24 divide-y divide-gray-100 dark:divide-gray-800/50">
        {isLoading ? (
          <>
            {[...Array(6)].map((_, i) => (
              <LoadingSkeleton key={i} />
            ))}
          </>
        ) : isError ? (
          <div className="flex flex-col items-center justify-center py-20 px-4">
            <p className="text-sm text-gray-500 mb-4">Failed to load news</p>
            <button
              onClick={() => refetch()}
              className="px-4 py-2 bg-gray-900 dark:bg-white text-white dark:text-black rounded-full text-sm font-medium"
            >
              Try Again
            </button>
          </div>
        ) : articles.length === 0 ? (
          <EmptyState ticker={selectedTicker} />
        ) : (
          <>
            {articles.map((article, index) => (
              <NewsCard key={article.id} article={article} index={index} />
            ))}
            
            {/* Load more trigger */}
            <div ref={loadMoreRef} className="py-6">
              {isFetchingNextPage && (
                <div className="flex justify-center">
                  <RefreshCw className="w-4 h-4 text-gray-300 animate-spin" />
                </div>
              )}
              {!hasNextPage && articles.length > 0 && (
                <p className="text-center text-xs text-gray-400">
                  End of feed
                </p>
              )}
            </div>
          </>
        )}
      </div>
    </div>
  );
}
